import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type PointerEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { useT } from '@/i18n/react';
import { CELL_COLORS, CELL_COLOR_KEYS, type CellColor } from '@/lib/cellColors';
import { generateColorTerritory, type ColorTerritoryCell } from '@/lib/colorTerritory';
import { dialTick } from '@/lib/dialHaptics';
import { DEFAULT_DIAL_TUNING, type DialTuning } from './cellColorDialTuning';
import './cellColorDial.css';

/** Cells rendered on each side of the centre (the rest is off-stage). */
const RADIUS = 6;
const SLOTS = Array.from({ length: RADIUS * 2 + 1 }, (_, index) => index - RADIUS);
/** A press that moves less than this is a tap, not a drag. */
const TAP_PX = 6;
/** Horizontal wheel/trackpad travel per step, px. */
const WHEEL_STEP_PX = 48;
const FRAME_MS = 1000 / 60;

interface Burst {
  id: number;
  color: string;
  cellWidth: number;
  centerX: number;
  centerY: number;
  cells: ColorTerritoryCell[];
}

type Motion =
  | { kind: 'idle' }
  | { kind: 'drag'; pointerId: number; startX: number; startPos: number; startAt: number; samples: { x: number; t: number }[]; moved: boolean }
  | { kind: 'spring'; target: number; velocity: number };

const mod = (value: number, size: number) => ((value % size) + size) % size;
const smoothstep = (x: number) => x * x * (3 - 2 * x);

/**
 * The cell colour DIAL (GRUNDO #105, Geri): an endless row of glossy hexagons
 * that can be stepped with the arrows or flung like a dial (drag + momentum +
 * snap). The cell under the split-hexagon reticle is magnified; the edges are
 * frosted glass. The choice is made when the dial settles.
 *
 * Rendering is imperative on purpose: a fixed set of slots is repositioned
 * every animation frame from one float position, without React re-renders.
 */
export function CellColorDial({
  active,
  onChoose,
  colors = CELL_COLOR_KEYS,
  tuning: tuningOverride,
}: {
  active: CellColor;
  onChoose: (color: CellColor) => void;
  colors?: readonly CellColor[];
  /** Playground overrides; production uses `DEFAULT_DIAL_TUNING`. */
  tuning?: Partial<DialTuning>;
}) {
  const t = useT();
  const tuning = { ...DEFAULT_DIAL_TUNING, ...tuningOverride };
  const tuningRef = useRef(tuning);
  tuningRef.current = tuning;
  const colorsRef = useRef(colors);
  colorsRef.current = colors;
  const activeRef = useRef(active);
  activeRef.current = active;
  const onChooseRef = useRef(onChoose);
  onChooseRef.current = onChoose;

  const stageRef = useRef<HTMLDivElement>(null);
  const slotRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const posRef = useRef(Math.max(0, colors.indexOf(active)));
  const motionRef = useRef<Motion>({ kind: 'idle' });
  const frameRef = useRef<number | null>(null);
  const lastFrameRef = useRef(0);
  const centerRef = useRef<number | null>(null);
  const wheelRef = useRef(0);
  const burstEndRef = useRef<number | null>(null);
  const burstIdRef = useRef(0);
  const [burst, setBurst] = useState<Burst | null>(null);
  const [centerName, setCenterName] = useState(CELL_COLORS[active].label);

  /** Positions every slot for the current float position. */
  const paint = useCallback(() => {
    const list = colorsRef.current;
    const size = list.length;
    if (size === 0) return;
    const tune = tuningRef.current;
    const pos = posRef.current;
    const base = Math.round(pos);
    const step = tune.itemSize * tune.spacing;
    // The magnified centre cell pushes its neighbours aside by its extra width.
    const push = ((tune.magnify - 1) * tune.itemSize) / 2;
    for (const [slot, offsetIndex] of SLOTS.entries()) {
      const element = slotRefs.current[slot];
      if (!element) continue;
      const index = base + offsetIndex;
      const offset = index - pos;
      const distance = Math.abs(offset);
      const lens = smoothstep(Math.max(0, 1 - distance / tune.lensWidth));
      const shrink = Math.max(0.25, 1 - tune.falloff * Math.max(0, distance - tune.lensWidth / 2));
      const scale = (1 + (tune.magnify - 1) * lens) * shrink;
      const x = offset * step + Math.sign(offset) * Math.min(distance, 1) * (push + tune.neighborGap);
      const opacity = 1 - (1 - tune.minOpacity) * Math.min(1, distance / (RADIUS - 1));
      // Depth of field: the focus is the reticle; cells further out are darker and softer.
      const depth = Math.max(0, distance - 0.5);
      const blur = tune.blurPerStep * Math.pow(depth, 1.25);
      const dim = Math.max(0.3, 1 - tune.dimPerStep * Math.pow(depth, 1.1));
      element.style.transform = `translate(-50%, -50%) translateX(${x.toFixed(2)}px) scale(${scale.toFixed(4)})`;
      element.style.opacity = opacity.toFixed(3);
      element.style.zIndex = String(100 - Math.round(distance * 10));
      element.style.setProperty('--focus', lens.toFixed(3));
      element.style.setProperty('--blur', `${blur.toFixed(2)}px`);
      element.style.setProperty('--dim', dim.toFixed(3));
      element.style.setProperty('--tilt', Math.max(-1.5, Math.min(1.5, offset)).toFixed(3));
      const key = list[mod(index, size)]!;
      if (element.dataset.color !== key) {
        element.dataset.color = key;
        element.style.setProperty('--ccolor', CELL_COLORS[key].hex);
        element.setAttribute('aria-label', CELL_COLORS[key].label);
        element.title = CELL_COLORS[key].label;
      }
      element.setAttribute('aria-pressed', String(key === activeRef.current));
    }
    // A new colour under the reticle while turning: tick, glint across it, and name it.
    if (centerRef.current !== base) {
      const turning = centerRef.current !== null && motionRef.current.kind !== 'idle';
      centerRef.current = base;
      const center = slotRefs.current[RADIUS];
      const key = list[mod(base, size)]!;
      setCenterName(CELL_COLORS[key].label);
      if (turning && tune.haptics) dialTick();
      if (turning && center && tune.glintMs > 0 && !reducedMotion()) {
        center.classList.remove('dial__slot--glint');
        void center.offsetWidth; // restart the animation
        center.classList.add('dial__slot--glint');
      }
    }
  }, []);

  const settle = useCallback((target: number) => {
    const list = colorsRef.current;
    const index = mod(target, list.length);
    posRef.current = index; // positions are equivalent modulo the palette size
    centerRef.current = index; // the same cell, renumbered — not a new arrival
    motionRef.current = { kind: 'idle' };
    paint();
    const key = list[index]!;
    if (key === activeRef.current) return;
    onChooseRef.current(key);
    const stage = stageRef.current;
    if (stage && tuningRef.current.burst && !reducedMotion()) {
      const box = stage.getBoundingClientRect();
      if (burstEndRef.current !== null) window.clearTimeout(burstEndRef.current);
      setBurst({
        id: ++burstIdRef.current,
        color: CELL_COLORS[key].hex,
        cellWidth: tuningRef.current.itemSize * 1.3,
        centerX: box.left + box.width / 2,
        centerY: box.top + box.height / 2,
        cells: generateColorTerritory(),
      });
      burstEndRef.current = window.setTimeout(() => setBurst(null), 5_000);
    }
  }, [paint]);

  const tick = useCallback((now: number) => {
    frameRef.current = null;
    const motion = motionRef.current;
    if (motion.kind !== 'spring') return;
    const frames = Math.min(4, (now - (lastFrameRef.current || now - FRAME_MS)) / FRAME_MS);
    lastFrameRef.current = now;
    const tune = tuningRef.current;
    motion.velocity += (motion.target - posRef.current) * tune.stiffness * frames;
    motion.velocity *= Math.pow(1 - tune.damping, frames);
    posRef.current += motion.velocity * frames;
    if (Math.abs(motion.target - posRef.current) < 0.002 && Math.abs(motion.velocity) < 0.002) {
      settle(motion.target);
      return;
    }
    paint();
    frameRef.current = window.requestAnimationFrame(tick);
  }, [paint, settle]);

  const springTo = useCallback((target: number, velocity = 0) => {
    if (reducedMotion()) {
      settle(target);
      return;
    }
    motionRef.current = { kind: 'spring', target, velocity };
    lastFrameRef.current = 0;
    if (frameRef.current === null) frameRef.current = window.requestAnimationFrame(tick);
  }, [settle, tick]);

  const stepBy = useCallback((delta: number) => {
    const motion = motionRef.current;
    const from = motion.kind === 'spring' ? motion.target : Math.round(posRef.current);
    springTo(from + delta, motion.kind === 'spring' ? motion.velocity : 0);
  }, [springTo]);

  // Paint on mount and whenever the look changes.
  useLayoutEffect(() => {
    paint();
  });

  // An outside change of the chosen colour (profile load, another device).
  useEffect(() => {
    if (motionRef.current.kind !== 'idle') return;
    const list = colorsRef.current;
    if (list[mod(Math.round(posRef.current), list.length)] === active) return;
    posRef.current = Math.max(0, list.indexOf(active));
    centerRef.current = null;
    paint();
  }, [active, paint]);

  // Horizontal trackpad / shift+wheel steps the dial; plain vertical scroll stays the page's.
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const onWheel = (event: WheelEvent) => {
      const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.shiftKey ? event.deltaY : 0;
      if (delta === 0) return;
      event.preventDefault();
      wheelRef.current += delta;
      while (Math.abs(wheelRef.current) >= WHEEL_STEP_PX) {
        const direction = Math.sign(wheelRef.current);
        wheelRef.current -= direction * WHEEL_STEP_PX;
        stepBy(direction);
      }
    };
    stage.addEventListener('wheel', onWheel, { passive: false });
    return () => stage.removeEventListener('wheel', onWheel);
  }, [stepBy]);

  useEffect(() => () => {
    if (frameRef.current !== null) window.cancelAnimationFrame(frameRef.current);
    if (burstEndRef.current !== null) window.clearTimeout(burstEndRef.current);
  }, []);

  function onPointerDown(event: PointerEvent<HTMLDivElement>) {
    if (event.button !== 0) return;
    if (frameRef.current !== null) window.cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
    motionRef.current = {
      kind: 'drag',
      pointerId: event.pointerId,
      startX: event.clientX,
      startPos: posRef.current,
      startAt: event.timeStamp,
      samples: [{ x: event.clientX, t: event.timeStamp }],
      moved: false,
    };
  }

  function onPointerMove(event: PointerEvent<HTMLDivElement>) {
    const motion = motionRef.current;
    if (motion.kind !== 'drag' || motion.pointerId !== event.pointerId) return;
    const dx = event.clientX - motion.startX;
    if (!motion.moved && Math.abs(dx) < TAP_PX) return;
    if (!motion.moved) {
      motion.moved = true;
      event.currentTarget.setPointerCapture(event.pointerId);
    }
    const tune = tuningRef.current;
    posRef.current = motion.startPos - dx / (tune.itemSize * tune.spacing);
    motion.samples.push({ x: event.clientX, t: event.timeStamp });
    while (motion.samples.length > 2 && event.timeStamp - motion.samples[0]!.t > 90) motion.samples.shift();
    paint();
  }

  function onPointerUp(event: PointerEvent<HTMLDivElement>) {
    const motion = motionRef.current;
    if (motion.kind !== 'drag' || motion.pointerId !== event.pointerId) return;
    const tune = tuningRef.current;
    if (!motion.moved) {
      // A tap: the tapped cell rolls to the centre.
      const slot = (event.target as HTMLElement).closest<HTMLElement>('[data-slot]');
      const offset = slot ? Number(slot.dataset.slot) : 0;
      motionRef.current = { kind: 'idle' };
      springTo(Math.round(posRef.current) + offset);
      return;
    }
    const first = motion.samples[0]!;
    const elapsed = Math.max(1, event.timeStamp - first.t);
    // Items per ms; the dial moves against the finger.
    const velocity = -((event.clientX - first.x) / (tune.itemSize * tune.spacing)) / elapsed;
    const target = Math.round(posRef.current + velocity * tune.inertiaMs);
    springTo(target, velocity * FRAME_MS * 0.5);
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      stepBy(-1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      stepBy(1);
    }
  }

  const magnified = tuning.itemSize * tuning.magnify;
  const style = {
    '--dial-item': `${tuning.itemSize}px`,
    '--dial-height': `${Math.round(magnified * 1.1547 * tuning.reticleScale + 40)}px`,
    '--dial-reticle': `${Math.round(magnified * tuning.reticleScale)}px`,
    '--dial-glow': `${tuning.glowSize}px`,
    '--dial-glow-a': tuning.glowStrength,
    '--dial-shine': tuning.shine,
    '--dial-glint-ms': `${tuning.glintMs}ms`,
    '--dial-facets': tuning.facets,
    '--dial-rim': `${tuning.reticleWidth}px`,
    '--dial-rim-glass': tuning.reticleGlass,
    '--dial-edge': `${tuning.edgeWidth}%`,
    '--dial-edge-blur': `${tuning.edgeBlur}px`,
  } as CSSProperties;

  return (
    <div className="dial" style={style}>
      {burst && typeof document !== 'undefined'
        ? createPortal(<TerritoryBurst key={burst.id} burst={burst} />, document.body)
        : null}
      <div
        ref={stageRef}
        className="dial__stage"
        role="group"
        aria-label={t.colorPicker.title}
        tabIndex={0}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onKeyDown={onKeyDown}
      >
        {SLOTS.map((offset, slot) => {
          // First-render colours (static markup, tests); `paint` takes over after mount.
          const key = colors[mod(Math.round(posRef.current) + offset, colors.length)] ?? active;
          return (
          <button
            key={offset}
            ref={(element) => { slotRefs.current[slot] = element; }}
            type="button"
            className="dial__slot"
            data-slot={offset}
            data-color-swatch
            data-color={key}
            aria-label={CELL_COLORS[key].label}
            style={{ '--ccolor': CELL_COLORS[key].hex } as CSSProperties}
            tabIndex={-1}
            onClick={(event) => {
              // Keyboard activation only; pointer taps are handled on release.
              if (event.detail === 0) springTo(Math.round(posRef.current) + offset);
            }}
          >
            <span className="dial__cell">
              <span className="dial__hex">
                <span className="dial__facets" />
                <span className="dial__gloss" />
                <span className="dial__glint" />
                <span className="dial__glint dial__glint--b" />
              </span>
            </span>
          </button>
          );
        })}
      </div>
      <span className="dial__edge dial__edge--left" aria-hidden="true" />
      <span className="dial__edge dial__edge--right" aria-hidden="true" />
      <Reticle gap={tuning.reticleGap} />
      <button type="button" className="dial__arrow dial__arrow--left" aria-label={t.colorPicker.previous} onClick={() => stepBy(-1)}>
        <ArrowIcon direction="left" />
      </button>
      <button type="button" className="dial__arrow dial__arrow--right" aria-label={t.colorPicker.next} onClick={() => stepBy(1)}>
        <ArrowIcon direction="right" />
      </button>
      <span className="dial__name" aria-live="polite">{centerName}</span>
    </div>
  );
}

/**
 * The "crosshair": a pointy-top hexagon split vertically into two halves, with
 * a gap and a short tick at the top and bottom, plus a glass lens inside.
 */
function Reticle({ gap }: { gap: number }) {
  const h = 115.47;
  const top = { x: 50 - 50 * gap, y: 28.87 * gap };
  const bottom = { x: 50 - 50 * gap, y: h - 28.87 * gap };
  const halves = [
    `M ${top.x} ${top.y} L 0 28.87 L 0 86.6 L ${bottom.x} ${bottom.y}`,
    `M ${100 - top.x} ${top.y} L 100 28.87 L 100 86.6 L ${100 - bottom.x} ${bottom.y}`,
    'M 50 -12 L 50 3',
    `M 50 ${h - 3} L 50 ${h + 12}`,
  ];
  return (
    <svg className="dial__reticle" viewBox="-8 -14 116 143.47" aria-hidden="true">
      <defs>
        <radialGradient id="dial-lens" cx="35%" cy="25%" r="80%">
          <stop offset="0%" stopColor="#fff" stopOpacity="0.16" />
          <stop offset="55%" stopColor="#fff" stopOpacity="0.03" />
          <stop offset="100%" stopColor="#fff" stopOpacity="0" />
        </radialGradient>
        {/* Glass rim: bright at the top-left, clearer in the middle, a second highlight at the bottom. */}
        <linearGradient id="dial-rim" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#fff" stopOpacity="0.95" />
          <stop offset="45%" stopColor="#fff" stopOpacity="0.38" />
          <stop offset="75%" stopColor="#fff" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#fff" stopOpacity="0.9" />
        </linearGradient>
        <filter id="dial-halo" x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="2.2" />
        </filter>
        <filter id="dial-soft" x="-10%" y="-10%" width="120%" height="120%">
          <feGaussianBlur stdDeviation="0.35" />
        </filter>
      </defs>
      <path className="dial__lens" d="M 50 0 L 100 28.87 L 100 86.6 L 50 115.47 L 0 86.6 L 0 28.87 Z" fill="url(#dial-lens)" />
      {halves.map((d) => <path key={`shade-${d}`} className="dial__rim-shade" d={d} />)}
      {halves.map((d) => <path key={`halo-${d}`} className="dial__rim-halo" d={d} filter="url(#dial-halo)" />)}
      {halves.map((d) => <path key={`body-${d}`} className="dial__rim-body" d={d} stroke="url(#dial-rim)" filter="url(#dial-soft)" />)}
      {halves.map((d) => <path key={`spec-${d}`} className="dial__rim-spec" d={d} />)}
      <path className="dial__reticle-half" d={halves[0]} />
      <path className="dial__reticle-half" d={halves[1]} />
    </svg>
  );
}

function TerritoryBurst({ burst }: { burst: Burst }) {
  const cellHeight = burst.cellWidth * 1.1547;
  return (
    <div className="dial__territory" aria-hidden="true">
      {burst.cells.map((cell) => (
        <span
          key={`${cell.q},${cell.r}`}
          className="dial__territory-cell"
          style={{
            '--ccolor': burst.color,
            '--cell-width': `${burst.cellWidth}px`,
            '--cell-height': `${cellHeight}px`,
            '--cell-x': `${burst.centerX + burst.cellWidth * (cell.q + cell.r / 2)}px`,
            '--cell-y': `${burst.centerY + cellHeight * 0.75 * cell.r}px`,
            '--cell-delay': `${cell.delayMs}ms`,
            '--cell-duration': `${cell.durationMs}ms`,
          } as CSSProperties}
        />
      ))}
    </div>
  );
}

function reducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
}

function ArrowIcon({ direction }: { direction: 'left' | 'right' }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d={direction === 'left' ? 'm15 5-7 7 7 7' : 'm9 5 7 7-7 7'}
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
